/**
 * Products Listing Page
 * Shows all available products from the search API (OpenSearch)
 */

import { useEffect } from 'react';
import styled from '@emotion/styled';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { useSearch } from '../lib/hooks/use-collections';
import { CollectionProduct } from '../domain/entities/collection.entity';
import { FunnelExperience, FunnelErrorBoundary } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '../lib/hooks/use-funnel-add-to-cart';

const PageContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-align: center;
  color: #292929;
`;

const Subtitle = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: #666;
`;

const Breadcrumb = styled.nav`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;

  a {
    color: #3b82f6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  background: #fef2f2;
  border-radius: 12px;
  color: #991b1b;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: ${({ theme }) => theme.mediaQueries.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #3b82f6;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1rem;
  background: #f3f4f6;
`;

const CardTitle = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #292929;
`;

const CardDescription = styled.p`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardPrice = styled.p`
  margin: 0;
  font-size: 1rem;
`;

function formatPrice(dollars: number, currencyFormat: string): string {
  const amount = dollars.toFixed(2);
  return `${currencyFormat}${amount}`;
}

export function ProductsPage() {
  const { t } = useTranslation();
  const { localizedPath, countryConfig } = useIntl();
  const [searchParams] = useSearchParams();
  const sym = countryConfig.currency.format;
  const { data, loading, error, search } = useSearch();

  const handleFunnelAddToCart = useFunnelAddToCart();
  const urlParameters = Object.fromEntries(searchParams.entries());

  useEffect(() => {
    search({ limit: 40 });
  }, [search]);

  const products: CollectionProduct[] = data?.products || [];
  const pagination = data?.pagination;

  return (
    <PageContainer>
      <Breadcrumb>
        <Link to="/">{t('navigation.home')}</Link> / {t('navigation.products')}
      </Breadcrumb>
      <Title>{t('productsPage.title')}</Title>
      <Subtitle>
        {t('productsPage.subtitle')}
        {pagination && ` ${t('productsPage.productCount', { count: pagination.total })}`}
      </Subtitle>

      {error && (
        <ErrorState>
          <p>{t('productsPage.errorLoading', { error })}</p>
        </ErrorState>
      )}

      {loading && products.length === 0 && (
        <LoadingState>{t('productsPage.loading')}</LoadingState>
      )}

      <ProductGrid>
        {products.map((product) => (
          <ProductCard
            key={product.id}
            to={localizedPath(`/products/${product.id}`)}
          >
            {product.imageUri && (
              <ProductImage src={product.imageUri} alt={product.title} />
            )}
            <div>
              <CardTitle>{product.title}</CardTitle>
              {product.description && (
                <CardDescription>{product.description}</CardDescription>
              )}
            </div>
            <div style={{ marginTop: 'auto' }}>
              <CardPrice>
                <span style={{ fontWeight: 'bold', color: '#D3373C' }}>
                  {product.priceMax > product.priceMin
                    ? `${formatPrice(product.priceMin, sym)} - ${formatPrice(product.priceMax, sym)}`
                    : formatPrice(product.priceMin, sym)}
                </span>
              </CardPrice>
            </div>
          </ProductCard>
        ))}
      </ProductGrid>

      <FunnelErrorBoundary resetKey={window.location.pathname}>
        <FunnelExperience
          pageType="collection"
          context={{ url: '/products', country: countryConfig.countryCode, urlParameters }}
          onAddToCart={handleFunnelAddToCart}
        />
      </FunnelErrorBoundary>
    </PageContainer>
  );
}
